import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UseFormReturn } from "react-hook-form";
import { Box, CircularProgress, Skeleton, Typography } from "@mui/material";
import {
  GetQuestionsFormData,
  ValidationChallengePayload,
} from "@interfaces";
import {
  BirthdateInput,
  BottomActionArea,
  BottomActionButton,
  SingleFormInput,
  NavigateBeforeButton,
  FormContentContainer,
  FormHeader,
  FormBody,
} from "@components";
import { capitalizeParametValue } from "@utils";
import { FormContainer } from "@styles/styled";

interface Props {
  form: UseFormReturn<GetQuestionsFormData>;
  showValidateDoctorDataInputs: boolean;
  requestQuestionSet: (payload: ValidationChallengePayload) => void;
  isCreatingValidationChallenge: boolean;
}

export const GetQuestions: React.FC<Props> = ({
  form,
  showValidateDoctorDataInputs,
  requestQuestionSet,
  isCreatingValidationChallenge,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
    setValue,
    trigger,
  } = form;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleBack = useCallback(() => {
    const requestId = searchParams.get("requestId");
    const goBackUrl = requestId
      ? `/app/solicitudes/${requestId}/receta/crear`
      : "/app/solicitudes";
    navigate(goBackUrl);
  }, [navigate, searchParams]);

  const onSubmit = useCallback(
    (data: GetQuestionsFormData) => {
      if (
        showValidateDoctorDataInputs &&
        (!data.name ||
          !data.firstSurname ||
          !data.secondSurname ||
          !data.birthdate)
      ) {
        return;
      }
      const validationChallengePayload: ValidationChallengePayload = {
        ...(showValidateDoctorDataInputs && {
          validatedDoctorData: {
            name: data.name,
            firstSurname: data.firstSurname,
            secondSurname: data.secondSurname,
            birthdate: data.birthdate,
          },
        }),
      };
      requestQuestionSet(validationChallengePayload);
    },
    [showValidateDoctorDataInputs, requestQuestionSet],
  );

  const hasAutoRequestedQuestions = useRef(false);

  useEffect(() => {
    if (showValidateDoctorDataInputs || hasAutoRequestedQuestions.current) {
      return;
    }
    hasAutoRequestedQuestions.current = true;
    onSubmit(form.getValues());
  }, [showValidateDoctorDataInputs, onSubmit, form]);

  if (!showValidateDoctorDataInputs) {
    return (
      <>
        <FormHeader
          title="Verificación de identidad"
          titleColor="white"
          left={<NavigateBeforeButton iconColor="white" onClick={handleBack} />}
        />
        <FormContentContainer disableGutters>
          <FormBody>
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 3 }}>
              <Skeleton variant="text" width="70%" height={28} />
              {[1, 2, 3].map((i) => (
                <Box key={i}>
                  <Skeleton variant="text" width="90%" height={24} sx={{ mb: 1 }} />
                  <Box sx={{ pl: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                    <Skeleton variant="rounded" width="80%" height={20} />
                    <Skeleton variant="rounded" width="75%" height={20} />
                    <Skeleton variant="rounded" width="85%" height={20} />
                  </Box>
                </Box>
              ))}
            </Box>
          </FormBody>
        </FormContentContainer>
      </>
    );
  }

  return (
    <>
      <FormHeader
        title="Verificación de identidad"
        titleColor="white"
        left={<NavigateBeforeButton iconColor="white" onClick={handleBack} />}
      />
      <FormContentContainer disableGutters>
        <FormBody>
          <Typography>
            {showValidateDoctorDataInputs
              ? "Ingresa los datos de tu cédula de identidad"
              : ""}
          </Typography>
          <FormContainer onSubmit={handleSubmit((data) => onSubmit(data))}>
            {showValidateDoctorDataInputs && (
              <>
                <SingleFormInput
                  title="Nombre completo"
                  parameterName="name"
                  errorText="Debes escribir tu nombre completo."
                  register={register}
                  errors={errors}
                  onBlur={() =>
                    capitalizeParametValue("name", watch, setValue, trigger)
                  }
                />
                <SingleFormInput
                  title="Apellido paterno"
                  parameterName="firstSurname"
                  errorText="Debes escribir tu apellido paterno."
                  register={register}
                  errors={errors}
                  onBlur={() =>
                    capitalizeParametValue(
                      "firstSurname",
                      watch,
                      setValue,
                      trigger,
                    )
                  }
                />
                <SingleFormInput
                  title="Apellido materno"
                  parameterName="secondSurname"
                  errorText="Debes escribir tu apellido materno."
                  register={register}
                  errors={errors}
                  autoComplete="off"
                  onBlur={() =>
                    capitalizeParametValue(
                      "secondSurname",
                      watch,
                      setValue,
                      trigger,
                    )
                  }
                />
                <BirthdateInput
                  control={control}
                  errorManagement={{ errors }}
                />
              </>
            )}

            <BottomActionArea>
              <BottomActionButton
                type="submit"
                disabled={isCreatingValidationChallenge}
              >
                {isCreatingValidationChallenge ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Siguiente"
                )}
              </BottomActionButton>
            </BottomActionArea>
          </FormContainer>
        </FormBody>
      </FormContentContainer>
    </>
  );
};
